// The project the user is working in, chosen once in the top bar and shared by every screen (Home,
// handovers, the calendar, Master Data, the team). Only the project's id is remembered on this
// device, as a convenience: the server still decides what the user may see in it.
import { useSyncExternalStore } from 'react';

const KEY = 'sivan:project';
const listeners = new Set<() => void>();

function read(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

let current: string | null = read();

/** Makes `projectId` the current project for every screen. */
export function chooseProject(projectId: string): void {
  if (current === projectId) return;
  current = projectId;
  try {
    window.localStorage.setItem(KEY, projectId);
  } catch {
    // Without storage the choice lasts until the page is reloaded.
  }
  for (const listener of listeners) listener();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * The current project among `projects` (the ones the user may see): the chosen one; otherwise the
 * first project in progress (not one still being set up or closed), so a first visit opens real
 * work instead of an empty project; otherwise the first.
 */
export function useCurrentProject<P extends { readonly id: string; readonly status?: string }>(
  projects: readonly P[],
): P | undefined {
  const chosen = useSyncExternalStore(subscribe, () => current);
  return (
    projects.find((p) => p.id === chosen) ??
    projects.find((p) => p.status === 'active') ??
    projects[0]
  );
}

/** Forgets the choice (between tests). */
export function forgetCurrentProject(): void {
  current = null;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Nothing kept.
  }
}
